import { handleSessionExpiration } from '@/components/Auth/auth.utils';

import store from '../../store/store';
import i18n from '../../utils/i18n/i18n';
import HttpError from './HttpError';
import { getUserLocale } from '../../utils/utils';
import { userManager } from '../../components/Auth/UserManager';
import { type ServiceName } from '../../store/store.type';

export type HttpMethod = 'GET' | 'POST' | 'PATCH' | 'DELETE' | 'PUT';

export const throwHttpErrors = (response: Response) => {
  const statusLimit = 310;
  if (response.status >= 200 && response.status < statusLimit) {
    return response;
  }
  const error = new HttpError(response.statusText);
  error.response = response;
  throw error;
};

export interface FetchRequestParams {
  url: string;
  method?: HttpMethod;
  headers?: Headers;
  /** Already serialized body (e.g. `JSON.stringify(payload)`) */
  body?: string;
  signal?: AbortSignal;
}

const BODY_METHODS = new Set<HttpMethod>(['PATCH', 'POST', 'PUT']);

const buildRequest = ({ url, method = 'GET', headers, body, signal }: FetchRequestParams): Request => {
  // Copy to avoid mutating the caller's headers, since the request is built again on a 401 replay
  const requestHeaders = new Headers(headers);
  if (BODY_METHODS.has(method)) {
    requestHeaders.set('Content-Type', 'application/json');
  }
  return new Request(url, { method, headers: requestHeaders, body, signal });
};

const sendRequest = async (params: FetchRequestParams): Promise<Response> => throwHttpErrors(await fetch(buildRequest(params)));

export const hasHttpStatus = (error: unknown, status: number): boolean => error instanceof HttpError && error.response?.status === status;

const isUnauthorized = (error: unknown): boolean => hasHttpStatus(error, 401);

export const fetchRequest = async (params: FetchRequestParams): Promise<Response> => {
  try {
    return await sendRequest(params);
  } catch (error) {
    if (!isUnauthorized(error)) {
      throw error;
    }
  }

  // On 401, silently renew the token and replay the request with the new access_token
  let accessToken: string | undefined;
  try {
    const user = await userManager.getUser().signinSilent();
    accessToken = user?.access_token;
  } catch (silentRenewError) {
    handleSessionExpiration();
    throw silentRenewError;
  }

  const headers = new Headers(params.headers);
  headers.set('Authorization', `Bearer ${accessToken}`);

  try {
    return await sendRequest({ ...params, headers });
  } catch (replayError) {
    if (isUnauthorized(replayError)) {
      handleSessionExpiration();
    }
    throw replayError;
  }
};

export const buildHeaders = (serviceName: ServiceName, customLocale?: string): Headers => {
  const state = store.getState();
  const headers = new Headers();
  const locale = customLocale ?? getUserLocale();

  const service = state.config.services[serviceName];

  headers.append('Authorization', `Bearer ${state.auth.token}`);
  if ('api_key' in service) {
    headers.append('x-api-key', service.api_key);
  }
  headers.append('accept-language', locale);

  return headers;
};

export const buildUrl = (serviceName: ServiceName, resourcePath: string) => `${store.getState().config.services[serviceName].url}/${resourcePath}`;

export const handleError = (errorDetails?: string | Error) => {
  let errorMessage = errorDetails;
  if (errorDetails instanceof Error) {
    errorMessage = errorDetails.message;
  }
  throw new Error(`${i18n.t('error.generic.message')}${errorMessage || ''}`);
};

export const handleHttpError = (errorMessage: string) => (httpError: any) => {
  if (httpError.response && httpError.response.json) {
    return httpError.response.json().then((error: any) => {
      throw error.message || error.error;
    });
  }
  return handleError(httpError.message || errorMessage);
};
